interface GridVertex {
    baseX: number;
    baseY: number;
    x: number;
    y: number;
}

interface GridTheme {
    lineColor: string;
    dotColor: string;
}

// one of these is created per canvas (the body background, the footer card,
// the contact header, ...) - bundling it here keeps the per-instance state in
// one typed place instead of a long list of closure variables
interface GridState {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    theme: GridTheme;

    width: number;
    height: number;
    cols: number;
    rows: number;

    offsetX: number;
    offsetY: number;
    dpr: number;
    phaseX: number;
    phaseY: number;

    // only vertices displaced by the cursor (or still easing back to rest)
    // are tracked here - every other vertex sits exactly at its base position
    active: Map<string, GridVertex>;

    // viewport-space (not canvas-local) so a stationary cursor over a
    // scrolling canvas (e.g. the footer card) still resolves to the right
    // local point - see step()
    lastClientX: number;
    lastClientY: number;
    hasMouse: boolean;

    interactive: boolean;
    running: boolean;
}

export default class BodyGrid {
    static readonly CELL_SIZE = 48;
    static readonly RADIUS = 180;
    static readonly STRENGTH = 28;
    static readonly EASE = 0.18;
    static readonly SETTLE_EPSILON = 0.05;
    // set to true to bring the magnet-pull dots back
    static readonly DOTS_ENABLED = false;
    static readonly DOT_RADIUS = 1.5;
    static readonly MAX_DPR = 2;
    // matches mobile/tablet devices and any narrow (<= 991px) viewport - the
    // magnet interaction is mouse-only, so it stays off there, but the grid
    // itself still renders (static, no cursor tracking)
    static readonly STATIC_ONLY_QUERY = '(max-width: 991px), (hover: none), (pointer: coarse)';

    // selected via data-body-grid-theme="dark" on a given canvas (e.g. the
    // footer card) - light lines/dots for use over a dark background
    static readonly THEMES: Record<string, GridTheme> = {
        light: { lineColor: 'rgba(0, 0, 0, 0.08)', dotColor: 'rgba(0, 0, 0, 0.35)' },
        dark: { lineColor: 'rgba(255, 255, 255, 0.22)', dotColor: 'rgba(255, 255, 255, 0.5)' },
    };

    static init(): void {
        const canvases: NodeListOf<HTMLCanvasElement> = document.querySelectorAll('[data-body-grid]');

        canvases.forEach((canvas) => this.canvasInit(canvas));
    }

    static canvasInit(canvas: HTMLCanvasElement): void {
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return;
        }

        const state: GridState = {
            canvas,
            ctx,
            theme: this.THEMES[canvas.dataset.bodyGridTheme ?? 'light'] ?? this.THEMES.light,
            width: 0,
            height: 0,
            cols: 0,
            rows: 0,
            offsetX: 0,
            offsetY: 0,
            dpr: 1,
            phaseX: 0,
            phaseY: 0,
            active: new Map(),
            lastClientX: -Infinity,
            lastClientY: -Infinity,
            hasMouse: false,
            interactive: this.isInteractive(),
            running: false,
        };

        this.resize(state);
        this.render(state);
        this.bindEvents(state);
    }

    static isInteractive(): boolean {
        return (
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
            !window.matchMedia(this.STATIC_ONLY_QUERY).matches
        );
    }

    static key(col: number, row: number): string {
        return `${col}:${row}`;
    }

    static getVertex(state: GridState, col: number, row: number): GridVertex {
        const existing = state.active.get(this.key(col, row));

        if (existing) {
            return existing;
        }

        const baseX = col * this.CELL_SIZE;
        const baseY = row * this.CELL_SIZE;

        return { baseX, baseY, x: baseX, y: baseY };
    }

    // the canvas isn't always viewport-fixed (e.g. the footer card scrolls
    // with the page) but its grid still has to line up with the fixed,
    // viewport-anchored grid around it - so instead of drawing vertices at
    // their raw canvas-local position, the whole draw transform is shifted
    // by the canvas's current offset modulo one cell ("phase"), keeping the
    // pattern pinned to the viewport no matter where the canvas itself sits
    static applyTransform(state: GridState): void {
        state.phaseX = ((state.offsetX % this.CELL_SIZE) + this.CELL_SIZE) % this.CELL_SIZE;
        state.phaseY = ((state.offsetY % this.CELL_SIZE) + this.CELL_SIZE) % this.CELL_SIZE;

        // a 1px (local-space) stroke is `dpr` device pixels wide once scaled -
        // for it to render crisp instead of a blurry 2px smear, the path needs
        // to land on a whole-device-pixel boundary (even width) or a
        // half-pixel one (odd width, e.g. dpr 1). CELL_SIZE (48) keeps
        // dpr * CELL_SIZE an integer for every realistic dpr, so snapping just
        // the translation is enough to pixel-align every resting grid line;
        // vertices displaced by the magnet are continuously-animated floats
        // and stay unsnapped on purpose, so the motion itself isn't stepped
        const snap = Math.round(state.dpr) % 2 === 1 ? 0.5 : 0;
        const tx = Math.round(-state.phaseX * state.dpr - snap) + snap;
        const ty = Math.round(-state.phaseY * state.dpr - snap) + snap;

        state.ctx.setTransform(state.dpr, 0, 0, state.dpr, tx, ty);
    }

    // re-reads the canvas's position on screen and re-applies the transform
    // if it changed (always, when `force` - e.g. after a resize; only if it
    // actually moved otherwise, so a scroll listener doesn't redraw every
    // single scroll tick of a canvas that hasn't gone anywhere). Returns
    // whether anything changed.
    static updatePosition(state: GridState, force = false): boolean {
        const rect = state.canvas.getBoundingClientRect();
        const moved = rect.left !== state.offsetX || rect.top !== state.offsetY;

        if (!force && !moved) {
            return false;
        }

        state.offsetX = rect.left;
        state.offsetY = rect.top;
        this.applyTransform(state);

        return true;
    }

    static resize(state: GridState): void {
        const rect = state.canvas.getBoundingClientRect();

        state.width = rect.width;
        state.height = rect.height;
        state.dpr = Math.min(window.devicePixelRatio || 1, this.MAX_DPR);
        state.canvas.width = Math.round(state.width * state.dpr);
        state.canvas.height = Math.round(state.height * state.dpr);
        this.updatePosition(state, true);
        // the phase shift can eat up to one cell off the left/top edge,
        // so pad the grid by an extra cell to still cover the full canvas
        state.cols = Math.ceil((state.width + state.phaseX) / this.CELL_SIZE) + 1;
        state.rows = Math.ceil((state.height + state.phaseY) / this.CELL_SIZE) + 1;
        state.active.clear();
    }

    // pulls every vertex near the cursor towards it, and eases every
    // still-displaced vertex back towards its resting position; returns
    // whether anything is still moving, so the render loop knows when it can stop
    static step(state: GridState): boolean {
        // derived fresh from the last known viewport mouse position every
        // time, instead of cached - so it's automatically correct even right
        // after the canvas scrolled under a cursor that didn't itself move
        const mouseX = state.lastClientX - state.offsetX;
        const mouseY = state.lastClientY - state.offsetY;

        if (state.hasMouse) {
            const minCol = Math.max(0, Math.floor((mouseX - this.RADIUS) / this.CELL_SIZE));
            const maxCol = Math.min(state.cols - 1, Math.ceil((mouseX + this.RADIUS) / this.CELL_SIZE));
            const minRow = Math.max(0, Math.floor((mouseY - this.RADIUS) / this.CELL_SIZE));
            const maxRow = Math.min(state.rows - 1, Math.ceil((mouseY + this.RADIUS) / this.CELL_SIZE));

            for (let row = minRow; row <= maxRow; row++) {
                for (let col = minCol; col <= maxCol; col++) {
                    const k = this.key(col, row);

                    if (!state.active.has(k)) {
                        state.active.set(k, this.getVertex(state, col, row));
                    }
                }
            }
        }

        let stillActive = false;

        state.active.forEach((vertex, k) => {
            const dx = vertex.baseX - mouseX;
            const dy = vertex.baseY - mouseY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const withinRadius = state.hasMouse && dist < this.RADIUS;

            let targetX = vertex.baseX;
            let targetY = vertex.baseY;

            if (withinRadius) {
                // pull the vertex towards the cursor, strongest at the centre
                // and fading out smoothly towards the edge of the radius
                const falloff = (1 - dist / this.RADIUS) ** 2;
                const safeDist = Math.max(dist, 0.001);

                targetX = vertex.baseX - (dx / safeDist) * this.STRENGTH * falloff;
                targetY = vertex.baseY - (dy / safeDist) * this.STRENGTH * falloff;
            }

            vertex.x += (targetX - vertex.x) * this.EASE;
            vertex.y += (targetY - vertex.y) * this.EASE;

            const settled =
                !withinRadius &&
                Math.abs(vertex.x - vertex.baseX) < this.SETTLE_EPSILON &&
                Math.abs(vertex.y - vertex.baseY) < this.SETTLE_EPSILON;

            if (settled) {
                state.active.delete(k);
            } else {
                stillActive = true;
            }
        });

        return stillActive;
    }

    // smooths a polyline through its points using quadratic curves between
    // consecutive midpoints, so the displaced grid reads as an elastic sheet
    // instead of a faceted zig-zag
    static tracePath(ctx: CanvasRenderingContext2D, points: GridVertex[]): void {
        if (points.length < 2) {
            return;
        }

        ctx.moveTo(points[0].x, points[0].y);

        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[i];
            const p1 = points[i + 1];
            const midX = (p0.x + p1.x) / 2;
            const midY = (p0.y + p1.y) / 2;

            ctx.quadraticCurveTo(p0.x, p0.y, midX, midY);
        }

        const last = points[points.length - 1];

        ctx.lineTo(last.x, last.y);
    }

    static render(state: GridState): void {
        const { ctx, canvas } = state;

        // clear the full pixel buffer under an identity transform first -
        // the draw transform below is phase-shifted, so clearRect(0, 0,
        // width, height) through it would miss a sliver at the edge
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        ctx.strokeStyle = state.theme.lineColor;
        ctx.lineWidth = 1;
        ctx.beginPath();

        for (let row = 0; row < state.rows; row++) {
            const points: GridVertex[] = [];

            for (let col = 0; col < state.cols; col++) {
                points.push(this.getVertex(state, col, row));
            }

            this.tracePath(ctx, points);
        }

        for (let col = 0; col < state.cols; col++) {
            const points: GridVertex[] = [];

            for (let row = 0; row < state.rows; row++) {
                points.push(this.getVertex(state, col, row));
            }

            this.tracePath(ctx, points);
        }

        ctx.stroke();

        // a dot only on vertices the magnet is currently pulling on (or
        // still easing back from it) - everywhere else stays plain lines
        if (this.DOTS_ENABLED && state.active.size > 0) {
            ctx.fillStyle = state.theme.dotColor;
            ctx.beginPath();

            state.active.forEach((vertex) => {
                ctx.moveTo(vertex.x + this.DOT_RADIUS, vertex.y);
                ctx.arc(vertex.x, vertex.y, this.DOT_RADIUS, 0, Math.PI * 2);
            });

            ctx.fill();
        }
    }

    static ensureLoop(state: GridState): void {
        if (state.running) {
            return;
        }

        state.running = true;

        const loop = (): void => {
            const stillActive = this.step(state);

            this.render(state);

            if (stillActive) {
                window.requestAnimationFrame(loop);
            } else {
                state.running = false;
            }
        };

        window.requestAnimationFrame(loop);
    }

    static handleMouseMove(state: GridState, event: MouseEvent): void {
        if (!state.interactive) {
            return;
        }

        state.lastClientX = event.clientX;
        state.lastClientY = event.clientY;
        state.hasMouse = true;
        this.ensureLoop(state);
    }

    static handleMouseOut(state: GridState, event: MouseEvent): void {
        if (!state.interactive || event.relatedTarget) {
            return;
        }

        state.hasMouse = false;
        this.ensureLoop(state);
    }

    // debounced - only recompute the whole grid once the window has
    // actually settled on a new size, not on every resize tick
    static handleResize(state: GridState): void {
        this.resize(state);
        this.render(state);
        state.interactive = this.isInteractive();
        state.hasMouse = false;
    }

    // throttled to once per frame - keeps a non-fixed canvas (e.g. inside
    // the footer, which scrolls with the page) phase-locked to the
    // viewport-anchored grid around it; a no-op for a position:fixed canvas,
    // whose offset never changes
    static handleScroll(state: GridState): void {
        if (!this.updatePosition(state)) {
            return;
        }

        if (state.interactive && state.hasMouse) {
            this.ensureLoop(state);
        } else {
            this.render(state);
        }
    }

    static bindEvents(state: GridState): void {
        window.addEventListener('mousemove', (event) => this.handleMouseMove(state, event));
        window.addEventListener('mouseout', (event) => this.handleMouseOut(state, event));

        let resizeTimer: number | undefined;

        window.addEventListener('resize', () => {
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => this.handleResize(state), 200);
        });

        let scrollScheduled = false;

        window.addEventListener(
            'scroll',
            () => {
                if (scrollScheduled) {
                    return;
                }

                scrollScheduled = true;
                window.requestAnimationFrame(() => {
                    scrollScheduled = false;
                    this.handleScroll(state);
                });
            },
            { passive: true },
        );
    }
}
